import React, { useState } from "react";
import { Check, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import EmployeeDeductions from "@/components/earnings/interfaces";

interface AddCustomRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (rate: CustomRate) => void;
  availableRateTypes: string[];
  availableDependentDeductions: EmployeeDeductions | null;
}

interface CustomRate {
  id?: number;
  name: string;
  rateType: string;
  unitType: string;
  multiplier?: number;
  fixedAmount?: number;
  dependentDeductionIds?: string[];
}

const unitTypes = [
  { value: "hourly", label: "Hourly" },
  { value: "day", label: "Day" },
];

const AddCustomRateModal: React.FC<AddCustomRateModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  availableRateTypes,
  availableDependentDeductions,
}) => {
  const [rate, setRate] = useState<CustomRate>({
    name: "",
    rateType: "",
    unitType: "",
    multiplier: undefined,
    fixedAmount: undefined,
    dependentDeductionIds: availableDependentDeductions?.deductions.map((d) =>
      d.id.toString(),
    ),
  });

  const handleChange = (field: keyof CustomRate, value: any) => {
    setRate((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeductionToggle = (deductionId: string) => {
    setRate((prev) => {
      const currentIds = prev.dependentDeductionIds || [];
      const newIds = currentIds.includes(deductionId)
        ? currentIds.filter((id) => id !== deductionId)
        : [...currentIds, deductionId];

      return { ...prev, dependentDeductionIds: newIds };
    });
  };

  const handleSubmit = () => {
    if (
      rate.name &&
      rate.rateType &&
      rate.unitType &&
      (rate.multiplier || rate.fixedAmount)
    ) {
      onAdd(rate);
      onClose();
    } else {
      alert("Please fill in all required fields");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Custom Rate</DialogTitle>
          <p className="text-sm text-muted-foreground pt-2">
            Define a custom rate for specific work types. You can specify either
            a multiplier based on the employee's standard hourly rate or a fixed
            amount per unit (hour/day). Select which existing deductions should
            apply to earnings calculated with this rate.
          </p>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={rate.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Rate name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <div className="flex items-center gap-1">
                <Label htmlFor="rateType">Rate Type</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Classification only, does not affect calculations.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select
                value={rate.rateType}
                onValueChange={(value) => handleChange("rateType", value)}
              >
                <SelectTrigger id="rateType">
                  <SelectValue placeholder="Select rate type" />
                </SelectTrigger>
                <SelectContent>
                  {availableRateTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="unitType">Unit Type</Label>
              <Select
                value={rate.unitType}
                onValueChange={(value) => handleChange("unitType", value)}
              >
                <SelectTrigger id="unitType">
                  <SelectValue placeholder="Select unit type" />
                </SelectTrigger>
                <SelectContent>
                  {unitTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <div className="flex items-center gap-1">
                <Label htmlFor="multiplier">Multiplier</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Multiplies the employee's standard hourly rate. Use this
                        OR Fixed Amount.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="multiplier"
                type="number"
                value={rate.multiplier || ""}
                onChange={(e) =>
                  handleChange(
                    "multiplier",
                    e.target.value ? parseFloat(e.target.value) : undefined,
                  )
                }
                placeholder="Enter multiplier"
              />
            </div>

            <div className="grid w-full items-center gap-1.5">
              <div className="flex items-center gap-1">
                <Label htmlFor="fixedAmount">Fixed Amount</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        A fixed payment amount per unit (hour/day). Use this OR
                        Multiplier.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="fixedAmount"
                type="number"
                value={rate.fixedAmount || ""}
                onChange={(e) =>
                  handleChange(
                    "fixedAmount",
                    e.target.value ? parseFloat(e.target.value) : undefined,
                  )
                }
                placeholder="Enter fixed amount"
              />
            </div>
          </div>

          {availableDependentDeductions &&
            availableDependentDeductions.deductions.length > 0 && (
              <div className="grid w-full items-center gap-1.5 mt-2 border-t pt-4">
                <Label className="font-medium">Applicable Deductions</Label>
                <p className="text-sm text-muted-foreground">
                  These earnings will be included in the calculation basis for
                  the following deductions:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                  {availableDependentDeductions.deductions.map((deduction) => {
                    const deductionId = deduction.id.toString();
                    const isChecked =
                      rate.dependentDeductionIds?.includes(deductionId) ||
                      false;

                    return (
                      <div
                        className="flex items-center space-x-2"
                        key={deduction.id}
                      >
                        <Checkbox
                          id={`deduction-${deduction.id}`}
                          checked={true}
                          disabled
                          aria-label={`${deduction.displayName} (deduction applies)`}
                        />
                        <label
                          htmlFor={`deduction-${deduction.id}`}
                          className="text-sm text-muted-foreground"
                        >
                          {deduction.displayName}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
        <DialogFooter className="sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Add Rate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomRateModal;
